/**
 * Slide link drag guard.
 *
 * Slides in the slideshow section can be made fully clickable by adding a `.slide__link`
 * overlay anchor. Because the slideshow is also drag-to-scroll, a pointer-drag that starts
 * on the anchor must not be treated as a click when the pointer is released.
 *
 * This module watches for pointer movement that begins on a slide link and suppresses the
 * navigation that would otherwise follow. The suppression self-expires so a genuine click
 * (or a keyboard activation, which never produces pointer movement) is never blocked.
 *
 * Loaded as a module so it only ever evaluates once per page, however many slides use it.
 */

/** Movement in CSS pixels, on either axis, that counts as a drag rather than a click. */
const DRAG_THRESHOLD = 8;

/** How long after a drag ends that clicks on a slide link stay suppressed, in milliseconds. */
const SUPPRESS_WINDOW = 400;

let suppressClicksUntil = 0;

/**
 * Returns the slide link the event originated from, if any.
 * @param {Event} event
 * @returns {Element | null}
 */
function getSlideLink(event) {
  const target = event.composedPath?.()[0] ?? event.target;

  if (!(target instanceof Element)) return null;

  return target.closest('.slide__link');
}

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!getSlideLink(event)) return;

    const startX = event.clientX;
    const startY = event.clientY;
    const controller = new AbortController();
    const { signal } = controller;

    let dragged = false;

    /** @param {PointerEvent} moveEvent */
    const onPointerMove = (moveEvent) => {
      if (dragged) return;

      if (
        Math.abs(moveEvent.clientX - startX) > DRAG_THRESHOLD ||
        Math.abs(moveEvent.clientY - startY) > DRAG_THRESHOLD
      ) {
        dragged = true;
      }
    };

    const onPointerEnd = () => {
      if (dragged) suppressClicksUntil = performance.now() + SUPPRESS_WINDOW;
      controller.abort();
    };

    document.addEventListener('pointermove', onPointerMove, { signal, passive: true });
    document.addEventListener('pointerup', onPointerEnd, { signal });
    document.addEventListener('pointercancel', onPointerEnd, { signal });
  },
  { capture: true, passive: true }
);

document.addEventListener(
  'click',
  (event) => {
    if (performance.now() > suppressClicksUntil) return;
    if (!getSlideLink(event)) return;

    // The pointer was dragged, so this is the tail end of a swipe, not a click on the slide.
    event.preventDefault();
  },
  { capture: true }
);
